import Api from 'fou-api'
import VuexErrors from 'src/vuex/modules/errors'
import Errors from 'src/utils/helper/errors'
import { Toaster } from 'src/utils/helper/toaster'
import i18n from 'src/assets/i18n/i18n'

const $t = i18n().global.t;
export default {
  namespaced: true,

  state () {
    return {
      results: [],
      uploading: false,
      getting: false,
      gettingAll: false,
      uploadStates: {
        UPLOADED: 'uploaded',
        COMPLETE: 'complete',
        FAILED: 'failed',
        SET: 'set'
      },
      scanStatus: null,
      errorShown: false,
      imagesWithMalware: []
    }
  },

  getters: {
    list: state => {
      return state.results
    },
    uploading: state => {
      return state.uploading
    },
    findByGraphicId: state => {
      return graphicId => state.results.filter(item => {
        return item.graphicId === graphicId
      })[0] || null
    },
    findByImageId: state => {
      return imageId => state.results.filter(item => {
        return item.image.id === imageId
      })[0] || null
    },
    states: state => {
      return state.uploadStates
    },
    scanStatus: state => {
      return state.scanStatus
    },
    getImagesWithMalware: state => {
      return state.imagesWithMalware
    }
  },

  mutations: {
    add (state, item) {
      /*
       * If item exists overwrite.
       * Else push.
       */
      if (typeof item.image !== 'object') {
        return
      }
      const exists = state.results.some((result, idx) => {
        if (result.image.id === item.image.id) {
          item.graphicId = result.graphicId ? result.graphicId : null
          state.results[idx] = item
          return true
        }
      })
      if (!exists) {
        state.results.push(item)
      }
    },
    remove(state, id) {
      /*
       * Find by image.id and remove.
       */
      state.results.some((result, idx) => {
        if (result.image.id === id) {
          state.results.splice(idx, 1)
          return true
        }
      })
    },
    clear (state) {
      state.results = []
    },
    uploadStart (state) {
      state.uploading = true
    },
    uploadComplete (state) {
      state.uploading = false
    },
    getStart (state) {
      state.getting = true
    },
    getComplete (state) {
      state.getting = false
    },
    getAllStart (state) {
      state.gettingAll = true
    },
    getAllComplete (state) {
      state.gettingAll = false
    },
    setScanStatus (state, status) {
      state.scanStatus = status
    },
    setErrorShown (state, status) {
      state.errorShown = status
    },
    setImagesWithMalware (state, images) {
      state.imagesWithMalware = images
    }
  },

  actions: {
    get ({ commit, state }, options) {
      return new Promise((resolve, reject) => {
        if (state.getting) {
          reject(Errors.create({
            message: 'Already getting'
          }))
        }
        commit('getStart')
        Api.Images.getImage(options.id, options.submissionId)
          .then((response) => {
            const status = response?.data?.versions
              ? state.uploadStates.COMPLETE
              : state.uploadStates.UPLOADED
            commit('add', {
              image: response.data,
              graphicId: options.graphicId,
              status
            })
            commit('getComplete')
            resolve()
          })
          .catch((error) => {
            commit('add', {
              image: {},
              graphicId: options.graphicId,
              status: state.uploadStates.FAILED
            })
            const err = Errors.create(error)
            commit('errors/add', err)
            commit('getComplete')
            reject(err)
          })
      })
    },
    getAll ({ commit, dispatch, state }, options = {}) {
      options = {
        submissionId: null,
        force: false,
        ...options
      };

      function handleMalwareScanStatus(collection) {
        const anyFailedAvStatusImage = collection.some(item => item.avStatus === 'false');
        const anyBlankAvStatusImage = collection.some(item => item.avStatus === '');
        const allTrueAvStatusImage = collection.every(item => item.avStatus === 'true');

        if (anyFailedAvStatusImage) {
          const filesWithFalseAvStatus = collection.filter(item => item.avStatus === 'false');
          filesWithFalseAvStatus.forEach(file => {
            dispatch('delete', { imageId: file.id, submissionId: options.submissionId })
          })
          commit('setScanStatus', false);
          if (!state.errorShown) {
            commit('setImagesWithMalware', filesWithFalseAvStatus);
            Toaster.notifyMalwareWarning($t('malwareScan.toastMessage'))
            commit('setErrorShown', true)
          }
        } else if (anyBlankAvStatusImage && state.scanStatus !== false) {
          commit('setScanStatus', '');
        } else if (allTrueAvStatusImage && state.scanStatus !== false) {
          commit('setScanStatus', true);
        }
      }

      return new Promise(resolve => {
        /**
         * Dont call if in progress.
         */
        if (state.gettingAll) {
          resolve()
        } else {
          /**
           * Commit start as event.
           */
          commit('getAllStart')
          /**
           * Dont call if already has images.
           */
          if (state.results.length > 0 && !options.force) {
            commit('getAllComplete')
            resolve()
          } else {
            /**
             * Call.
             */
            Api.Images.getImages(options.submissionId)
              .then((response) => {
                if (response.data.collection.length > 0) {
                  for (const item of response.data.collection) {
                    let status;
                    if (item.uploadSuccessful === true) {
                      if (Array.isArray(item.versions) || item.avStatus === 'false') {
                        status = state.uploadStates.COMPLETE;
                      } else {
                        status = state.uploadStates.UPLOADED;
                      }
                    } else {
                      status = state.uploadStates.FAILED;
                    }
                    commit('add', {
                      image: item,
                      status
                    })
                  }
                  handleMalwareScanStatus(response.data.collection);
                }
                commit('getAllComplete')
                resolve(response.data)
              })
              .catch(error => {
                commit('errors/add', Errors.create(error))
                commit('getAllComplete')
                resolve()
              })
          }
        }
      })
    },
    upload({ commit, state }, options) {
      return new Promise(resolve => {
        commit('setScanStatus', '');
        if (state.uploading) {
          resolve()
        }
        commit('uploadStart')

        Api.Images.uploadImage(options.submissionId, { images: options.submits })
          .then((response) => {
            if (response.data.collection.length > 0) {
              commit('setImagesWithMalware', []);
              const images = response.data.collection;
              let complete = 0;
              state.errorShown = false;
              function _done(obj) {
                commit('add', obj);
                if (++complete === images.length) {
                  commit('uploadComplete');
                  resolve(obj);
                }
              }

              for (const image of images) {
                const status = Array.isArray(image.versions)
                  ? state.uploadStates.COMPLETE
                  : state.uploadStates.UPLOADED;
                const submit = options.submits.find(s => s.md5Checksum === image.md5Checksum);
                _done({
                  image,
                  graphicId: submit.graphicId,
                  status
                });
              }
            } else {
              for (const submit of options.submits) {
                _done({
                  image: {},
                  graphicId: submit.graphicId,
                  status: state.uploadStates.FAILED
                });
              }
            }
          })
          .catch(error => {
            function _done(obj) {
              commit('add', obj);
              commit('uploadComplete');
              resolve(obj);
            }
            
            if (error.response && error.response.status === 415) {
              const customError = { message: 'The file must be a PNG or JPEG image.' };
              const err = Errors.create(customError);
              commit('errors/add', err);
              commit('uploadComplete');
            } else {
              const err = Errors.create(error);
              commit('errors/add', err);
              for (const submit of options.submits) {
                _done({
                  image: {},
                  graphicId: submit.graphicId,
                  status: state.uploadStates.FAILED
                });
              }
            }
          })
      })
    },
    delete ({ commit }, options) {
      return new Promise((resolve, reject) => {
        commit('remove', options.imageId)
        Api.Images.deleteImage(options.imageId, options.submissionId)
          .then((response) => {
            resolve()
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    }
  },

  modules: {
    errors: VuexErrors
  }
}
